<template>
  <div
    data-qa="payment_club"
    class="payment-club"
    :class="{ 'payment-club_club-border': isClubEnabled }"
  >
    <div class="payment-club__inner d-flex flex-column gr-3">
      <div class="rounded elevation-1">
        <div class="d-flex flex-column gr-4 rounded-t bg-club py-4 px-5">
          <ClubSwitch
            v-model="isClubEnabled"
            data-qa="payment_club_switch"
            title="Клубная цена"
            :discount="clubOfferData?.clubDiscount"
          />
          <div>
            <div
              v-if="clubCardDateEnd"
              class="text-body-2 text-secondary--text"
            >
              Вы в клубе до <span data-qa="payment_club_card_date_end">{{ clubCardDateEnd }}</span> 👑
            </div>
            <ClubInfo>
              <template #activator="{ on }">
                <span
                  data-qa="payment_club_more_activator"
                  class="text-body-2 primary--text cursor-pointer"
                  @click.stop="on.click"
                >
                  Подробнее о клубе
                </span>
              </template>
            </ClubInfo>
          </div>
        </div>

        <PriceDetails
          :full-price="fullPrice"
          :lpu-price="clubOfferData?.lpuPrice"
          :option-price="clubOfferData?.couponPrice"
          :total-price="clubOfferData?.totalPrice"
          :card-price="clubOfferData?.cardPrice"
          :is-no-card="!clubOfferData?.cardDateEnd"
          :is-club-enabled="isClubEnabled"
        />

        <div
          v-if="isClubEnabled"
          class="px-5 pb-5"
        >
          <v-checkbox
            ref="acceptedOptionNonRefundableRef"
            v-model="clubDataStore.isAcceptedOptionNonRefundable"
            data-qa="payment_club_accepted_option_non_refundable_checkbox"
            class="payment-club__checkbox mt-0 pt-0 mb-5"
            :rules="[acceptedOptionNonRefundableRule]"
          >
            <template #label>
              <span class="text-body-2 text--text">
                Принимаю условие, что опцион на скидку невозвратный
              </span>
            </template>
            <template #message="{ message }">
              <span class="ml-8">
                {{ message }}
              </span>
            </template>
          </v-checkbox>
          <slot :is-club-enabled="isClubEnabled" />
        </div>
      </div>

      <ClubDiscountBanner
        v-if="!isClubEnabled"
        :full-price="fullPrice"
        :total-price="clubOfferData?.totalPrice"
        :card-price="clubOfferData?.cardPrice"
        @click="isClubEnabled = true"
      />

      <slot
        v-if="!isClubEnabled"
        :is-club-enabled="isClubEnabled"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { ru } from 'date-fns/locale'
import { format, parseISO } from 'date-fns'
import { PAYMENT_TYPES } from 'www/vue_components/common/AppointmentPage/constants'
import { useClubDataStore } from 'www/vue_components/common/AppointmentPage/stores'
import type { ClubOfferData } from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/components/PaymentInfo/components/PaymentClub/types'
import ClubDiscountBanner from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/components/PaymentInfo/components/PaymentClub/components/ClubDiscountBanner.vue'
import ClubInfo from 'src/components/common/ClubInfo/ClubInfo.vue'
import ClubSwitch from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/components/PaymentInfo/components/PaymentClub/components/ClubSwitch.vue'
import PriceDetails from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/components/PaymentInfo/components/PaymentClub/components/PriceDetails/PriceDetails.vue'

const props = defineProps<{
  fullPrice: number
  clubOfferData?: ClubOfferData
}>()

const clubDataStore = useClubDataStore()

const isClubEnabled = computed({
  get() {
    return clubDataStore.selectedPaymentType === PAYMENT_TYPES.club
  },
  set(value: boolean) {
    if (value) {
      clubDataStore.setStateSelectedPaymentType(PAYMENT_TYPES.club)
    } else {
      clubDataStore.setStateSelectedPaymentType(PAYMENT_TYPES.lpu)
    }
  },
})

const clubCardDateEnd = computed(() => {
  if (!props.clubOfferData?.cardDateEnd) {
    return
  }

  return format(parseISO(props.clubOfferData.cardDateEnd), 'dd.MM.yyyy', { locale: ru })
})

watch(isClubEnabled, () => {
  clubDataStore.isAcceptedOptionNonRefundable = false
})

const acceptedOptionNonRefundableRef = ref()
const acceptedOptionNonRefundableRule = (v: boolean) => (v || !isClubEnabled.value || 'Необходимо дать согласие')

function validate(): true | HTMLElement {
  if (acceptedOptionNonRefundableRef.value) {
    if (!acceptedOptionNonRefundableRef.value.validate(true)) {
      return acceptedOptionNonRefundableRef.value.$el
    }
  }

  return true
}

defineExpose({
  validate,
})
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/variables';

.payment-club {
  padding: 1px;

  &_club-border {
    border-radius: $border-radius-md;
    background-image: $club-coral-linear-gradient;
  }

  &_club-border &__inner {
    overflow: hidden;
    background-color: $ui-kit-bg-gray-0;
    border-radius: $border-radius-md;
  }

  &__checkbox :deep(.v-input__slot) {
    align-items: start;
  }
}
</style>
